(() => {
  const feeTiers = [
    { maximum: 20, variantId: 47490981691554, perBox: false },
    { maximum: 50, variantId: 47490981724322, perBox: false },
    { maximum: 100, variantId: 47490981757090, perBox: false },
    { maximum: 200, variantId: 47490981789858, perBox: false },
    { maximum: 500, variantId: 47490981822626, perBox: true },
    { maximum: Infinity, variantId: 47490981855394, perBox: true },
  ];

  const root = () => window.Shopify?.routes?.root || '/';

  const readJsonResponse = async (response, errorMessage) => {
    const responseText = await response.text();
    if (!response.ok) throw new Error(errorMessage);

    try {
      return JSON.parse(responseText);
    } catch {
      throw new Error(errorMessage);
    }
  };

  const submitNativeRemoval = (lineKeys) => {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = `${root()}cart/update`;
    form.acceptCharset = 'UTF-8';
    form.enctype = 'multipart/form-data';
    form.hidden = true;

    const appendHiddenInput = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    appendHiddenInput('form_type', 'cart');
    appendHiddenInput('utf8', '✓');
    appendHiddenInput('return_to', `${window.location.pathname}${window.location.search}`);
    lineKeys.forEach((lineKey) => appendHiddenInput(`updates[${lineKey}]`, '0'));

    document.body.appendChild(form);
    form.submit();
  };

  const fetchCart = async () => {
    const response = await fetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const cart = await readJsonResponse(response, 'The cart could not be checked.');
    if (!Array.isArray(cart?.items)) throw new Error('The cart could not be checked.');
    return cart;
  };

  const validateCart = (cart) => {
    const groups = new Map();
    const issues = [];

    (cart?.items || []).forEach((item) => {
      if (item.handle === 'paper-drawer-jewelry-boxes-with-inserts' && (!Number.isInteger(Number(item.quantity)) || Number(item.quantity) < 20)) {
        issues.push({ type: 'drawer-jewelry-minimum', item });
      }
      const role = item.properties?._slate_logo_role;
      if (item.handle === 'paper-drawer-jewelry-boxes-with-inserts' && item.properties?.['Logo Printing'] === 'Custom Logo' && role !== 'base') {
        issues.push({ type: 'drawer-jewelry-missing-logo-group', item });
      }
      if (role !== 'base' && role !== 'fee') return;

      const group = item.properties?._slate_logo_group;
      if (!group) {
        issues.push({ type: 'missing-group', item });
        return;
      }

      if (!groups.has(group)) groups.set(group, { bases: [], fees: [] });
      groups.get(group)[role === 'base' ? 'bases' : 'fees'].push(item);
    });

    groups.forEach(({ bases, fees }, group) => {
      if (bases.length !== 1 || fees.length !== 1) {
        issues.push({ type: 'missing-or-duplicate-line', group });
        return;
      }

      const base = bases[0];
      const fee = fees[0];
      if (base.handle === 'paper-drawer-jewelry-boxes-with-inserts' && (Number(base.properties?._slate_logo_location_count) !== 1 || base.properties?.['Logo Placement'] !== 'A — Outside Top Panel')) {
        issues.push({ type: 'drawer-jewelry-single-top-panel', group });
      }
      const boxQuantity = Number(base.quantity);
      const locationCount = Number(base.properties?._slate_logo_location_count);
      const feeLocationCount = Number(fee.properties?._slate_logo_location_count);
      const tier = feeTiers.find((candidate) => boxQuantity <= candidate.maximum);
      const expectedFeeQuantity = tier?.perBox ? boxQuantity * locationCount : locationCount;

      if (
        !Number.isInteger(boxQuantity)
        || boxQuantity < 20
        || ![1, 2].includes(locationCount)
        || feeLocationCount !== locationCount
        || Number(fee.variant_id) !== tier?.variantId
        || Number(fee.quantity) !== expectedFeeQuantity
      ) {
        issues.push({ type: 'incorrect-fee', group });
      }
    });

    return { valid: issues.length === 0, issues };
  };

  const applyCheckoutGuard = (cart) => {
    const result = validateCart(cart);

    document.querySelectorAll('[data-slate-logo-integrity-warning]').forEach((warning) => {
      warning.hidden = result.valid;
    });

    document.querySelectorAll('[data-slate-logo-cart-form] button[name="checkout"]').forEach((button) => {
      button.disabled = !result.valid;
      button.toggleAttribute('aria-disabled', !result.valid);
    });

    document.querySelectorAll('[data-slate-logo-accelerated]').forEach((container) => {
      container.hidden = !result.valid;
    });

    return result;
  };

  const readServerRenderedGuardState = () => {
    const warnings = [...document.querySelectorAll('[data-slate-logo-integrity-warning]')];
    if (!warnings.length) {
      return {
        valid: null,
        issues: [{ type: 'cart-check-unavailable' }],
        source: 'server-rendered',
      };
    }

    const invalid = warnings.some((warning) => !warning.hidden);
    return {
      valid: !invalid,
      issues: invalid ? [{ type: 'server-rendered-invalid' }] : [],
      source: 'server-rendered',
    };
  };

  const refreshCheckoutGuard = async (providedCart) => {
    try {
      const cart = providedCart?.items ? providedCart : await fetchCart();
      return applyCheckoutGuard(cart);
    } catch {
      return readServerRenderedGuardState();
    }
  };

  window.SlateLogoCartIntegrity = { validateCart, applyCheckoutGuard, refreshCheckoutGuard };

  document.addEventListener('DOMContentLoaded', () => refreshCheckoutGuard());
  document.documentElement.addEventListener('cart:change', (event) => refreshCheckoutGuard(event.detail?.cart));
  document.documentElement.addEventListener('cart:refresh', () => window.setTimeout(() => refreshCheckoutGuard(), 0));
  document.addEventListener('cart-drawer:refreshed', () => refreshCheckoutGuard());

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target.closest('[data-slate-logo-cart-form]');
      if (!form || event.submitter?.name !== 'checkout') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (form.dataset.slateLogoCheckoutBusy === 'true') return;

      form.dataset.slateLogoCheckoutBusy = 'true';
      event.submitter?.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const cart = await fetchCart();
        const result = applyCheckoutGuard(cart);

        if (!result.valid) {
          const warning = form.querySelector('[data-slate-logo-integrity-warning]');
          warning?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return;
        }

        window.location.assign(`${root()}checkout`);
      } catch {
        window.alert('We could not verify the logo printing charge. Please wait a moment and try checkout again.');
      } finally {
        delete form.dataset.slateLogoCheckoutBusy;
        event.submitter?.removeAttribute('aria-busy');
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    },
    true
  );

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-slate-remove-logo-group]');
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const group = button.dataset.slateRemoveLogoGroup;
    if (!group || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const lineKeys = [...new Set(
        [...document.querySelectorAll('[data-slate-logo-group][data-slate-logo-line-key]')]
          .filter((lineItem) => lineItem.dataset.slateLogoGroup === group)
          .map((lineItem) => lineItem.dataset.slateLogoLineKey)
          .filter(Boolean)
      )];
      if (!lineKeys.length) throw new Error('The customized set could not be identified. Please refresh the page and try again.');

      const updates = Object.fromEntries(lineKeys.map((lineKey) => [lineKey, 0]));

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      }));

      const requestBody = { updates };
      if (sectionsToBundle.length) {
        requestBody.sections = sectionsToBundle.join(',');
        requestBody.sections_url = window.location.pathname;
      }

      let response;
      try {
        response = await fetch(`${root()}cart/update.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });
      } catch {
        submitNativeRemoval(lineKeys);
        return;
      }
      if (!response.ok) {
        submitNativeRemoval(lineKeys);
        return;
      }

      let updatedCart;
      try {
        updatedCart = await readJsonResponse(response, 'The customized set could not be removed.');
      } catch (error) {
        if (!response.ok) throw error;
        window.location.reload();
        return;
      }
      if (!Array.isArray(updatedCart?.items)) {
        window.location.reload();
        return;
      }
      applyCheckoutGuard(updatedCart);

      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
      } else if (sectionsToBundle.length && updatedCart.sections) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'line-item:change',
            cart: updatedCart,
          },
        }));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      button.removeAttribute('aria-busy');
      window.alert(error.message || 'The customized set could not be removed. Please try again.');
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  });
})();
